import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { FileText, X, BookOpen, GraduationCap, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCHOLAR_URL = "https://scholar.google.com/citations?user=WypJ78AAAAAJ&hl=en&oi=sra";

// Scholar links keyed by paper title (partial match) for cross-linking
const SCHOLAR_LINKS = {
  "Soldier Vote":        "https://scholar.google.com/scholar?q=The+Soldier+Vote+War+Politics+Ballot+America+Inbody",
  "Coercive Strategic":  "https://scholar.google.com/scholar?q=Coercive+Strategic+Effect+Air+Power+Sea+Power+Inbody",
  "Flexible Credibility":"https://scholar.google.com/scholar?q=Flexible+Credibility+Conventional+Alternatives+Nuclear+Inbody",
  "Grand Strategy":      "https://scholar.google.com/scholar?q=Grand+Strategy+Crisis+Management+US+Foreign+Policy+Inbody",
  "Perceptions of War":  "https://scholar.google.com/scholar?q=Misperceptions+War+Korean+Inbody",
  "Obama":               "https://scholar.google.com/scholar?q=Obama+Legacy+International+Relations+Inbody",
};

function getScholarLink(title) {
  for (const [key, url] of Object.entries(SCHOLAR_LINKS)) {
    if (title.includes(key)) return url;
  }
  return null;
}

const ALL_CATEGORIES = ["Naval History", "Political Science", "Voting", "Military Affairs", "Radio", "Writing", "Books", "Music"];

export default function Papers() {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [viewingPaper, setViewingPaper] = useState(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Academic Papers & Manuscripts — Donald S. Inbody",
      "description": "Unpublished academic manuscripts and working papers by Donald S. Inbody on civil-military relations, military voting rights, naval history, and American political science.",
      "url": "https://inbody.net/papers",
      "author": {
        "@type": "Person",
        "name": "Donald S. Inbody",
        "url": "https://inbody.net",
        "jobTitle": "Political Scientist, Naval Historian"
      }
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  const { hash } = useLocation();

  const handleReadPaper = (paper) => {
    setViewingPaper(paper);
    // Increment view counter (fire-and-forget) and update local state
    axios.post(`${API}/papers/${paper.id}/view`)
      .then(() => {
        setPapers(prev => prev.map(p =>
          p.id === paper.id ? { ...p, views: (p.views ?? 0) + 1 } : p
        ));
      })
      .catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API}/papers`)
      .then(r => {
        setPapers(r.data);
        // auto-open paper from URL hash e.g. /papers#100-watts-and-a-wire
        if (hash) {
          const slug = hash.replace('#', '');
          const match = r.data.find(p =>
            p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === slug
          );
          if (match) setViewingPaper(match);
        }
      })
      .catch(() => setPapers([]))
      .finally(() => setLoading(false));
  }, [hash]);

  const filtered = activeCategory === "All"
    ? papers
    : papers.filter(p => (p.categories || [p.category]).includes(activeCategory));

  const usedCategories = ["All", ...ALL_CATEGORIES.filter(c => papers.some(p => (p.categories || [p.category]).includes(c)))];

  return (
    <div className="bg-[#F5F0E8]">
      <SEO
        title="Papers & Manuscripts — Donald Stephen Inbody"
        description="Unpublished manuscripts and academic papers by Donald S. Inbody covering naval history, political science, civil-military relations, military voting rights, coercive strategy, air power, sea power, and amateur radio — including '100 Watts and a Wire'."
        canonical="https://inbody.net/papers"
        keywords="Donald Inbody papers, naval history manuscripts, political science research, military affairs, civil-military relations, voting rights, military voting, coercive strategy, air power, sea power, 100 Watts and a Wire, amateur radio papers"
      />
      <Navbar />

      {/* Page Header */}
      <section className="pt-32 pb-16 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="overline text-[#C5A059] mb-3">Scholarship</p>
          <h1 data-testid="papers-page-title" className="font-serif text-5xl font-black text-[#F5F0E8] mb-4">
            Academic Papers &amp; Manuscripts
          </h1>
          <p className="text-[#F5F0E8]/60 max-w-xl leading-relaxed">
            Unpublished research, working papers, and manuscripts from Donald S. Inbody spanning naval history, political science, military affairs, and more.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b border-[#0A1628]/10 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-0 overflow-x-auto scrollbar-hide">
            {usedCategories.map(cat => (
              <button
                key={cat}
                data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-5 py-4 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  activeCategory === cat
                    ? "border-[#C5A059] text-[#0A1628]"
                    : "border-transparent text-[#2D3142]/50 hover:text-[#0A1628]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Papers List */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-24">
              <div className="w-8 h-8 border-2 border-[#0A1628] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="papers-empty" className="text-center py-24 border border-[#0A1628]/10 bg-white">
              <BookOpen size={36} className="text-[#C5A059] mx-auto mb-4" />
              <h3 className="font-serif text-xl text-[#0A1628] mb-2">No papers yet</h3>
              <p className="text-[#2D3142]/60 text-sm">Papers and manuscripts will appear here as they are added.</p>
            </div>
          ) : (
            <div data-testid="papers-list" className="space-y-4">
              {filtered.map(paper => (
                <article
                  key={paper.id}
                  data-testid={`paper-card-${paper.id}`}
                  className="bg-white border border-[#0A1628]/10 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-start gap-6 hover:border-[#0A1628]/25 transition-colors group"
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 w-12 h-12 bg-[#0A1628] flex items-center justify-center">
                    <FileText size={20} className="text-[#C5A059]" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      {(paper.categories || [paper.category]).map(cat => (
                        <span key={cat} className="text-xs bg-[#F5F0E8] text-[#2D3142] border border-[#0A1628]/10 px-2 py-1 uppercase tracking-widest font-medium">
                          {cat}
                        </span>
                      ))}
                      <span className="text-xs text-[#2D3142]/50 font-mono">{paper.year}</span>
                      {!paper.has_pdf && (
                        <span className="text-xs text-[#C5A059] border border-[#C5A059]/30 px-2 py-0.5">PDF Pending</span>
                      )}
                    </div>
                    <h2 className="font-serif text-xl font-bold text-[#0A1628] mb-3 leading-snug group-hover:text-[#2D3142] transition-colors">
                      {paper.title}
                    </h2>
                    <p className="text-[#2D3142] text-sm leading-relaxed line-clamp-3">
                      {paper.abstract}
                    </p>
                    {/* Read count */}
                    {paper.views !== undefined && (
                      <div className="flex items-center gap-1.5 text-[#2D3142]/35 text-xs mt-3">
                        <Eye size={11} />
                        <span data-testid={`paper-view-count-${paper.id}`}>{paper.views.toLocaleString()} reads</span>
                      </div>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="flex-shrink-0 flex flex-col gap-2">
                    {(paper.has_pdf || paper.pdf_url) ? (
                      <button
                        data-testid={`read-paper-${paper.id}`}
                        onClick={() => handleReadPaper(paper)}
                        className="inline-flex items-center gap-2 bg-[#0A1628] text-[#F5F0E8] px-5 py-2.5 text-sm font-semibold hover:bg-[#2D3142] transition-colors"
                      >
                        <FileText size={14} /> Read Paper
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-2 border border-[#0A1628]/20 text-[#2D3142]/40 px-5 py-2.5 text-sm font-semibold cursor-not-allowed">
                        Coming Soon
                      </span>
                    )}
                    {getScholarLink(paper.title) && (
                      <a
                        href={getScholarLink(paper.title)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`scholar-link-${paper.id}`}
                        className="inline-flex items-center gap-2 border border-[#0A1628]/20 text-[#2D3142]/60 px-5 py-2 text-xs font-medium hover:border-[#0A1628] hover:text-[#0A1628] transition-colors"
                      >
                        <GraduationCap size={12} /> Google Scholar
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      {/* PDF Viewer Modal */}
      {viewingPaper && (
        <div
          data-testid="pdf-viewer-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={e => { if (e.target === e.currentTarget) setViewingPaper(null); }}
        >
          <div className="bg-white w-full max-w-5xl mx-4 flex flex-col shadow-2xl"
            style={{ height: "90vh" }}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-[#0A1628] flex-shrink-0">
              <div className="min-w-0 mr-4">
                <h2 className="text-[#F5F0E8] font-serif font-bold truncate">{viewingPaper.title}</h2>
                <p className="text-[#F5F0E8]/50 text-xs mt-0.5">{viewingPaper.year} · {(viewingPaper.categories || [viewingPaper.category]).join(", ")}</p>
              </div>
              <button
                data-testid="close-pdf-viewer"
                onClick={() => setViewingPaper(null)}
                className="text-[#F5F0E8]/60 hover:text-[#F5F0E8] transition-colors flex-shrink-0 p-1"
              >
                <X size={20} />
              </button>
            </div>
            {/* Document viewer — PDF inline, DOCX via Google Docs Viewer */}
            {(() => {
              const docUrl = viewingPaper.pdf_url
                ? viewingPaper.pdf_url
                : `${API}/papers/${viewingPaper.id}/pdf`;
              const isDocx = docUrl.toLowerCase().includes('.docx');
              const src = isDocx
                ? `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(docUrl)}`
                : `${docUrl}#toolbar=0&navpanes=0&scrollbar=1`;
              return (
                <iframe
                  src={src}
                  title={viewingPaper.title}
                  className="flex-1 w-full border-0"
                />
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
